import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, CheckCircle, ArrowRight } from 'lucide-react';

const UNI_GOLD = '#d4a843';
const UNI_DARK = '#0a0a14';
const TX = 'rgba(255,255,255,0.88)';
const MU = 'rgba(255,255,255,0.45)';

interface ModuleMilestoneProps {
  isOpen: boolean;
  onClose: () => void;
  moduleTitle: string;
  degreeName: string;
}

export const ModuleMilestone: React.FC<ModuleMilestoneProps> = ({ isOpen, onClose, moduleTitle, degreeName }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(6, 6, 12, 0.95)',
              backdropFilter: 'blur(12px)'
            }}
          />

          {/* Certificate-like Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 100 }}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '540px',
              background: UNI_DARK,
              border: `1px solid rgba(212, 168, 67, 0.3)`,
              borderRadius: '24px',
              padding: '60px 40px',
              textAlign: 'center',
              boxShadow: '0 30px 60px rgba(0,0,0,0.8), inset 0 0 100px rgba(212, 168, 67, 0.05)',
              overflow: 'hidden'
            }}
          >
            {/* Elegant corner patterns */}
            <div style={{ position: 'absolute', top: 0, left: 0, width: 60, height: 60, borderTop: `2px solid ${UNI_GOLD}`, borderLeft: `2px solid ${UNI_GOLD}`, opacity: 0.3 }} />
            <div style={{ position: 'absolute', top: 0, right: 0, width: 60, height: 60, borderTop: `2px solid ${UNI_GOLD}`, borderRight: `2px solid ${UNI_GOLD}`, opacity: 0.3 }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, width: 60, height: 60, borderBottom: `2px solid ${UNI_GOLD}`, borderLeft: `2px solid ${UNI_GOLD}`, opacity: 0.3 }} />
            <div style={{ position: 'absolute', bottom: 0, right: 0, width: 60, height: 60, borderBottom: `2px solid ${UNI_GOLD}`, borderRight: `2px solid ${UNI_GOLD}`, opacity: 0.3 }} />

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: 'spring' }}
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(212, 168, 67, 0.1)',
                border: `2px solid ${UNI_GOLD}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 32px',
                color: UNI_GOLD
              }}
            >
              <Award size={40} />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              style={{
                fontSize: '12px',
                fontWeight: 800,
                color: UNI_GOLD,
                letterSpacing: '4px',
                textTransform: 'uppercase',
                margin: '0 0 16px'
              }}
            >
              Academic Milestone Reached
            </motion.h2>

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              style={{
                fontSize: '28px',
                fontWeight: 900,
                color: '#fff',
                letterSpacing: '-1px',
                margin: '0 0 12px',
                lineHeight: 1.2
              }}
            >
              {moduleTitle}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              style={{
                fontSize: '16px',
                color: MU,
                maxWidth: '380px',
                margin: '0 auto 40px',
                lineHeight: 1.6
              }}
            >
              You have successfully demonstrated mastery of this module within the {degreeName} programme.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <button
                onClick={onClose}
                style={{
                  background: `linear-gradient(135deg, ${UNI_GOLD}, #b8912e)`,
                  color: '#06060c',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 32px',
                  fontSize: '15px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  transition: 'all 0.2s',
                  boxShadow: '0 8px 20px rgba(212, 168, 67, 0.3)'
                }}
                onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                Continue Academic Journey <ArrowRight size={18} />
              </button>
              
              <button
                onClick={onClose}
                style={{
                  background: 'transparent',
                  color: MU,
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                Return to Dashboard
              </button>
            </motion.div>

            {/* Subtle glow effect */}
            <div style={{
              position: 'absolute',
              top: '-10%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100%',
              height: '120px',
              background: 'radial-gradient(ellipse at center, rgba(212, 168, 67, 0.15) 0%, transparent 70%)',
              pointerEvents: 'none'
            }} />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
